import { FormEvent } from 'react';

interface Step3Props {
  data: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const COURT_TYPES = [
  { value: 'Full glass walls', icon: 'ri-window-line' },
  { value: 'Panoramic glass', icon: 'ri-landscape-line' },
  { value: 'Wall or concrete', icon: 'ri-building-2-line' },
  { value: 'Other', icon: 'ri-question-line' },
];

const SURFACES = [
  { value: 'Artificial turf', icon: 'ri-leaf-line' },
  { value: 'Concrete', icon: 'ri-road-map-line' },
  { value: 'Clay', icon: 'ri-contrast-drop-line' },
  { value: 'Other', icon: 'ri-question-line' },
];

const CONDITIONS = ['New', 'Good', 'Fair'];

const FEATURES = [
  { value: 'Court lighting', icon: 'ri-flashlight-line' },
  { value: 'Balls provided', icon: 'ri-basketball-line' },
  { value: 'Racquets provided', icon: 'ri-tennis-ball-line' },
  { value: 'Indoor', icon: 'ri-home-office-line' },
];

function RadioCard({
  value, selected, onClick, icon, small
}: {
  value: string; selected: boolean; onClick: () => void; icon?: string; small?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer w-full ${
        small ? 'flex-1 text-center justify-center' : ''
      } ${
        selected
          ? 'bg-[#234012] border-[#234012] text-[#fcfbf7]'
          : 'bg-white border-[#e0d9ce] text-[#3a3a34] hover:border-[#b6862d]'
      }`}
    >
      {icon && <i className={`${icon} text-base shrink-0`} />}
      <span className="text-sm font-sans text-left leading-snug">{value}</span>
    </button>
  );
}

function CheckCard({
  value, checked, onClick, icon
}: {
  value: string; checked: boolean; onClick: () => void; icon: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer w-full ${
        checked
          ? 'bg-[#234012]/5 border-[#234012] text-[#234012]'
          : 'bg-white border-[#e0d9ce] text-[#3a3a34] hover:border-[#b6862d]'
      }`}
    >
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
        checked ? 'bg-[#234012] border-[#234012]' : 'border-[#d0c9be]'
      }`}>
        {checked && <i className="ri-check-line text-[10px] text-white" />}
      </div>
      <i className={`${icon} text-sm shrink-0`} />
      <span className="text-sm font-sans text-left leading-snug">{value}</span>
    </button>
  );
}

function toggleFeature(current: string, value: string): string {
  const arr = current ? current.split(',').map((s) => s.trim()).filter(Boolean) : [];
  return arr.includes(value) ? arr.filter((s) => s !== value).join(', ') : [...arr, value].join(', ');
}

export default function Step3Court({ data, onChange, onNext, onBack }: Step3Props) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!data.courtType || !data.surface || !data.condition) return;
    onNext();
  };

  return (
    <div className="w-full max-w-xl">
      <p className="text-[#b6862d] text-xs tracking-widest uppercase font-sans mb-5">Step 3 of 6</p>
      <h2 className="font-serif text-4xl md:text-5xl text-[#141413] mb-3 leading-tight">The padel court.</h2>
      <p className="text-[#77776f] text-sm font-sans leading-relaxed mb-10">
        The court is the centrepiece of your listing.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-7">
        {/* Court type */}
        <div className="flex flex-col gap-2.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Court Type</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {COURT_TYPES.map((ct) => (
              <RadioCard
                key={ct.value}
                value={ct.value}
                icon={ct.icon}
                selected={data.courtType === ct.value}
                onClick={() => onChange('courtType', ct.value)}
              />
            ))}
          </div>
        </div>

        {/* Surface */}
        <div className="flex flex-col gap-2.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Surface</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SURFACES.map((s) => (
              <RadioCard
                key={s.value}
                value={s.value}
                icon={s.icon}
                selected={data.surface === s.value}
                onClick={() => onChange('surface', s.value)}
              />
            ))}
          </div>
        </div>

        {/* Condition */}
        <div className="flex flex-col gap-2.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Condition</label>
          <div className="flex gap-2">
            {CONDITIONS.map((c) => (
              <RadioCard
                key={c}
                value={c}
                selected={data.condition === c}
                onClick={() => onChange('condition', c)}
                small
              />
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="flex flex-col gap-2.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Features</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FEATURES.map((f) => {
              const checked = !!(data.courtFeatures && data.courtFeatures.includes(f.value));
              return (
                <CheckCard
                  key={f.value}
                  value={f.value}
                  icon={f.icon}
                  checked={checked}
                  onClick={() => onChange('courtFeatures', toggleFeature(data.courtFeatures || '', f.value))}
                />
              );
            })}
          </div>
        </div>

        {/* Year installed */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Year Installed
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <input
            type="text"
            name="yearInstalled"
            value={data.yearInstalled || ''}
            onChange={(e) => onChange('yearInstalled', e.target.value)}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
            placeholder="e.g. 2021"
          />
        </div>

        {/* Court story */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Court Story
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <textarea
            name="courtStory"
            value={data.courtStory || ''}
            onChange={(e) => onChange('courtStory', e.target.value)}
            maxLength={500}
            rows={3}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="How did the court come about? Any memorable games played here? Context we can use in your listing..."
          />
        </div>

        {/* Nearby clubs */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Nearby Clubs
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <input
            type="text"
            name="nearbyClubs"
            value={data.nearbyClubs || ''}
            onChange={(e) => onChange('nearbyClubs', e.target.value)}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
            placeholder="e.g. Club de Pádel Marbella — 5 mins drive"
          />
        </div>

        <div className="flex items-center gap-4 mt-2">
          <button type="button" onClick={onBack} className="text-sm font-sans text-[#77776f] hover:text-[#3a3a34] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-arrow-left-line" /> Back
          </button>
          <button
            type="submit"
            disabled={!data.courtType || !data.surface || !data.condition}
            className="bg-[#234012] text-[#fcfbf7] text-sm font-sans font-medium px-8 py-4 rounded-lg hover:bg-[#305a18] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
